import { Waveform as WaveformIcon } from '@phosphor-icons/react';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { CircleIconButton } from '@/components/ui/circle-icon-button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuth } from '@/hooks/use-auth';
import { handleError } from '@/lib/errors';
import { cn } from '@/lib/utils';

interface VoiceInputProps {
  onTranscript: (text: string) => void;
  disabled?: boolean;
  /** Visual treatment override merged onto the CircleIconButton. Sizing stays in
   * the primitive so send/voice controls share the composer touch contract. */
  className?: string;
}

const MAX_DURATION_MS = 30_000;

export const VoiceInput = memo(function VoiceInput({
  onTranscript,
  disabled,
  className,
}: VoiceInputProps) {
  const { session } = useAuth();
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const stopRecording = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setIsRecording(false);
  }, []);

  // onTranscript stored in a ref so handlePointerDown's closure never needs to
  // reference transcribeAudio (which used to live in the deps array). This
  // sidesteps an SWC/HMR ordering quirk where the dev-build occasionally hoists
  // handlePointerDown above transcribeAudio and throws "Cannot access
  // 'transcribeAudio' before initialization" on every render.
  const onTranscriptRef = useRef(onTranscript);
  useEffect(() => {
    onTranscriptRef.current = onTranscript;
  }, [onTranscript]);

  const transcribeAudio = useCallback(
    async (blob: Blob) => {
      try {
        const formData = new FormData();
        formData.append('file', blob, 'voice.webm');
        formData.append('model', 'whisper-1');

        const resp = await fetch('/api/transcribe', {
          method: 'POST',
          // No Content-Type here — the browser sets the multipart boundary for
          // FormData automatically; setting it manually breaks the boundary.
          headers: session?.access_token
            ? { Authorization: `Bearer ${session.access_token}` }
            : undefined,
          body: formData,
        });

        if (!resp.ok) {
          handleError(new Error('Transcription request failed'), {
            feature: 'voice-input',
            page: '/',
          });
          return;
        }

        const data = (await resp.json()) as { text?: string };
        if (!data.text?.trim()) {
          toast.info("Could you try again? I couldn't make out any words");
          return;
        }

        onTranscriptRef.current(data.text.trim());
      } catch {
        handleError(new Error('Transcription failed'), { feature: 'voice-input', page: '/' });
      }
    },
    [session?.access_token],
  );

  // Keep transcribeAudio reachable in handlePointerDown via a ref, breaking
  // the closure dependency cycle that was confusing the SWC dev compiler.
  const transcribeAudioRef = useRef(transcribeAudio);
  useEffect(() => {
    transcribeAudioRef.current = transcribeAudio;
  }, [transcribeAudio]);

  const handlePointerDown = useCallback(
    async (e?: React.PointerEvent<HTMLButtonElement>) => {
      if (disabled) return;
      // D5 fix: ignore synthetic / keyboard-originated activations. Some browsers
      // dispatch a phantom pointerdown alongside a keyboard-triggered click when
      // focus lands on this button after the textarea sends a message via Enter.
      // Real pointer events have a non-empty `pointerType` ('mouse'|'touch'|'pen')
      // and a positive width/height; keyboard-synthesized ones come through as
      // pointerType='' with width/height=0.
      const pointerType = e?.pointerType as string | undefined;
      if (e && pointerType === '' && e.width === 0 && e.height === 0) {
        return;
      }
      chunksRef.current = [];

      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        streamRef.current = stream;

        const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
        mediaRecorderRef.current = recorder;

        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) chunksRef.current.push(e.data);
        };

        recorder.onstop = () => {
          const blob = new Blob(chunksRef.current, { type: 'audio/webm;codecs=opus' });
          if (blob.size < 1000) {
            toast.info("I didn't catch that — try holding a bit longer");
            return;
          }
          transcribeAudioRef.current(blob);
        };

        recorder.start();
        setIsRecording(true);

        timerRef.current = setTimeout(() => {
          stopRecording();
        }, MAX_DURATION_MS);
      } catch (err) {
        // Browser's getUserMedia throws when the user denies the mic prompt or
        // blocks the origin. That's an expected user action, not a bug. Show a
        // friendly toast; do NOT route through handleError (which fans out to
        // Sentry and treats it as a real issue).
        const isPermissionDenied =
          err instanceof DOMException &&
          (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError');
        if (isPermissionDenied) {
          toast.info('Microphone is blocked. Allow mic access in your browser to use voice input.');
        } else {
          // Everything else (device busy, no mic, unsupported codec) IS worth
          // capturing — still swallow the nested stack since the user can't act
          // on it, but keep the signal in Sentry.
          handleError(err instanceof Error ? err : new Error('Microphone unavailable'), {
            feature: 'voice-input',
            page: '/',
          });
        }
        setIsRecording(false);
      }
    },
    [disabled, stopRecording],
  );

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, []);

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          {/* DO NOT add border / ring / bg-muted around this button. The mic must render
              as an icon-only control so it doesn't compete with the adjacent filled Send
              button. A subtle hover wash is fine; a persistent wrapper is not. */}
          <CircleIconButton
            size="composer"
            ariaLabel={isRecording ? 'Recording — release to send' : 'Hold to record voice'}
            className={cn(
              'relative shrink-0 max-md:size-11',
              isRecording && 'text-destructive',
              className,
            )}
            onPointerDown={handlePointerDown}
            onPointerUp={stopRecording}
            onPointerLeave={stopRecording}
            // D5 fix: hold-to-record is a pointer-only gesture. Enter/Space on
            // a focused button shouldn't spawn the recording sheet — that was
            // happening when focus jumped here after the textarea cleared.
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') e.preventDefault();
            }}
            disabled={disabled}
          >
            {isRecording && (
              <span className="absolute inset-0 rounded-full border-2 border-destructive animate-pulse" />
            )}
            <WaveformIcon weight={isRecording ? 'fill' : 'regular'} />
          </CircleIconButton>
        </TooltipTrigger>
        <TooltipContent>{isRecording ? 'Release to send' : 'Hold to record voice'}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
});
